import React from "react";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  LabelList,
  Tooltip,
  ReferenceLine,
} from "recharts";
import axios from "axios";
import { Brand } from "@/components/Layout";
import { INTERPRETATION, BESOINS_T } from "@/data/needs";
import { computeResults, clinicalSynthesis, ecartLabel } from "@/lib/scoring";
import { PatientClosing } from "@/components/patient/PatientClosing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICK_INK = { fill: "#6E5C53", fontSize: 11 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };
const TICK_DARK = { fill: "#4A3B36", fontSize: 12 };
const SAT_DOMAIN = [0, 20];
const GAP_DOMAIN = [-20, 20];
const GAP_MARGIN = { left: 20, right: 40 };
const BAR_RADIUS = [4, 4, 4, 4];
const LABEL_STYLE = { fill: "#6E5C53", fontSize: 11 };

const gapColor = (gap) => ecartLabel(gap).color;

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-6">
    <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{children}</h3>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

const RankCard = ({ title, items, valueKey, suffix, accent, note, signed, emptyText }) => (
  <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-5 print-break">
    <h4 className="font-serif-title text-2xl mb-1" style={{ color: accent }}>{title}</h4>
    {note && <p className="text-xs text-[var(--ink-soft)] mb-3">{note}</p>}
    <ul className="space-y-2 mt-2">
      {items.map((r, i) => {
        const v = r[valueKey];
        const display = signed && v > 0 ? `+${v}` : `${v}`;
        return (
          <li key={r.key} className="flex items-center justify-between">
            <span className="text-[var(--ink)]"><span className="text-[var(--gold)] font-semibold mr-2">{i + 1}.</span>{r.short}</span>
            <span className="font-semibold" style={{ color: accent }}>{display}{suffix}</span>
          </li>
        );
      })}
      {items.length === 0 && <li className="text-sm text-[var(--ink-soft)]">{emptyText}</li>}
    </ul>
  </div>
);

const RadarPanel = ({ title, sub, data, color, thresholds }) => (
  <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 print-break">
    <SectionTitle sub={sub}>{title}</SectionTitle>
    <ResponsiveContainer width="100%" height={340}>
      <RadarChart data={data} outerRadius="72%">
        <PolarGrid stroke="#d8ccbe" />
        <PolarAngleAxis dataKey="subject" tick={TICK_INK} />
        <PolarRadiusAxis domain={SAT_DOMAIN} tick={TICK_MUTED} angle={90} />
        {thresholds.map((th) => (
          <Radar key={th.key} dataKey={th.key} stroke={th.color} strokeDasharray="4 4" strokeWidth={1.5} fill="none" dot={false} isAnimationActive={false} />
        ))}
        <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.45} strokeWidth={2} />
        <Tooltip formatter={(v, name) => (name === "value" ? [`${v}/20`, ""] : null)} />
      </RadarChart>
    </ResponsiveContainer>
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3">
      {thresholds.map((th) => (
        <span key={th.key} className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
          <span className="inline-block w-5 h-0 border-t-2 border-dashed" style={{ borderColor: th.color }} />
          {th.label}
        </span>
      ))}
    </div>
  </div>
);

const GapChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={380}>
    <BarChart data={data} layout="vertical" margin={GAP_MARGIN}>
      <XAxis type="number" domain={GAP_DOMAIN} tick={TICK_INK} />
      <YAxis type="category" dataKey="name" width={110} tick={TICK_DARK} />
      <ReferenceLine x={0} stroke="#a1907f" />
      <Tooltip formatter={(v) => [`${v > 0 ? "+" : ""}${v}`, ""]} />
      <Bar dataKey="gap" radius={BAR_RADIUS} minPointSize={3}>
        {data.map((d) => (<Cell key={d.name} fill={gapColor(d.gap)} />))}
        <LabelList dataKey="gap" position="right" formatter={(v) => `${v > 0 ? "+" : ""}${v}`} style={LABEL_STYLE} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

export const Results = ({ satisfaction, estimated, onRestart, lang }) => {
  const t = BESOINS_T[lang];
  const THRESHOLDS = [
    { key: "t5", color: "#FFC355", label: t.thresholds.t5 },
    { key: "t10", color: "#E99F4F", label: t.thresholds.t10 },
    { key: "t15", color: "#D4A77B", label: t.thresholds.t15 },
  ];

  const results = React.useMemo(() => computeResults(satisfaction, estimated, lang), [satisfaction, estimated, lang]);
  const synthesis = React.useMemo(() => clinicalSynthesis(results, lang), [results, lang]);

  React.useEffect(() => {
    axios.post(`${API}/evaluations`, { satisfaction, estimated }).catch((e) => console.error("save evaluation failed", e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const withThresholds = (valueKey) => results.rows.map((r) => ({ subject: r.short, value: r[valueKey], t5: 5, t10: 10, t15: 15 }));
  const measuredData = withThresholds("measured");
  const estimatedData = withThresholds("estimated");
  const gapData = [...results.rows].sort((a, b) => b.ecart - a.ecart).map((r) => ({ name: r.short, gap: r.ecart }));

  return (
    <div className="animate-fade-up print-container" data-testid="results-view">
      <div className="hidden print:block mb-6"><Brand /></div>

      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{t.thanks}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.thanksText}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <RadarPanel title={t.radar1} sub={t.radar1Sub} data={measuredData} color="#B58C67" thresholds={THRESHOLDS} />
        <RadarPanel title={t.radar2} sub={t.radar2Sub} data={estimatedData} color="#8A6D56" thresholds={THRESHOLDS} />
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break" data-testid="score-interpretation">
        <SectionTitle sub={t.interpSub}>{t.interpTitle}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INTERPRETATION.map((r) => (
            <div key={r.min} className="flex items-center gap-3 rounded-xl bg-[var(--sand)] border border-[var(--border)] p-3">
              <span className="inline-flex items-center justify-center h-9 w-16 rounded-lg text-white text-sm font-semibold shrink-0" style={{ backgroundColor: r.color }}>
                {r.min}–{r.max}
              </span>
              <span className="text-sm text-[var(--ink)]">{r[lang]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={t.gapSub}>{t.gapTitle}</SectionTitle>
        <p className="text-sm text-[var(--ink-soft)] text-center max-w-2xl mx-auto mb-4">{t.gapText}</p>
        <GapChart data={gapData} />
      </div>

      <div className="mb-10 print-break">
        <SectionTitle>{t.recapTitle}</SectionTitle>
        <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
          <table className="w-full text-sm" data-testid="recap-table">
            <thead>
              <tr className="bg-[var(--gold)] text-white text-left">
                <th className="p-3 font-medium">{t.colNeed}</th>
                <th className="p-3 font-medium text-right">{t.colMeasured}</th>
                <th className="p-3 font-medium text-right">{t.colEstimated}</th>
                <th className="p-3 font-medium">{t.colGap}</th>
              </tr>
            </thead>
            <tbody>
              {results.byEcartAbs.map((r, i) => (
                <tr key={r.key} className={i % 2 ? "bg-white/40" : "bg-white/70"}>
                  <td className="p-3 text-[var(--ink)]">{r.short}</td>
                  <td className="p-3 text-right font-semibold" style={{ color: r.interpretation.color }}>{r.measured}</td>
                  <td className="p-3 text-right text-[var(--ink)]">{r.estimated}</td>
                  <td className="p-3">
                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: r.ecartInfo.color }}>
                      {r.ecart > 0 ? "+" : ""}{r.ecart} · {r.ecartInfo.label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <RankCard title={t.rankResources} note={t.rankResourcesNote} items={results.topResources} valueKey="measured" suffix="/20" accent="#8A6D56" emptyText={t.noneCat} />
        <RankCard title={t.rankFragile} note={t.rankFragileNote} items={results.fragile} valueKey="measured" suffix="/20" accent="#B0553B" emptyText={t.noneCat} />
        <RankCard title={t.rankOver} note={t.rankOverNote} items={results.overestimated} valueKey="ecart" suffix="" accent="#E99F4F" signed emptyText={t.noneCat} />
        <RankCard title={t.rankUnder} note={t.rankUnderNote} items={results.underestimated} valueKey="ecart" suffix="" accent="#B0553B" signed emptyText={t.noneCat} />
      </div>
      <p className="text-xs text-[var(--ink-soft)] text-center mb-10 max-w-2xl mx-auto">{t.ecartNote}</p>

      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-9 mb-10 print-break">
        <h3 className="font-serif-title text-3xl mb-4">{t.synthTitle}</h3>
        <p className="leading-relaxed text-[0.98rem]" data-testid="clinical-synthesis">{synthesis}</p>
      </div>

      <PatientClosing lang={lang} />

      <div className="flex flex-wrap items-center justify-center gap-4 no-print">
        <button onClick={() => window.print()} data-testid="btn-export-pdf" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.exportPdf}</button>
        <button onClick={onRestart} data-testid="btn-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.restart}</button>
      </div>
    </div>
  );
};
